import { createReducer } from "../reducerUtil"
import {
  ADD_DICE_ROLL_RECORD,
  CLEAR_DICE_ROLL_HISTORY,
  ADD_PLAYER,
  UPDATE_PLAYER,
  DELETE_PLAYER,
  SET_ACTIVE_PLAYER,
  UPDATE_PLAYER_CONTROLS,
  UPDATE_CONTROL_CONFIG,
  UPDATE_PLAYER_DICE,
  SET_ROLLING_STATE,
} from "./constants"

const MAX_GROUPS = 50

const createDefaultControl = () => ({
  id: 1,
  dieType: "D20",
  quantity: 1,
  selectedRollType: "Attack",
  abilityMod: 0,
  proficiency: 0,
  proficiencyType: "Normal",
  bonus: 0,
  bonusText: "",
  targetValue: "",
  rollMethod: "Normal",
})

const initialState = {
  players: [
    {
      id: 1,
      label: "P1",
      color: "#D8C59A",
      history: [],
      dice: [],
      controls: [createDefaultControl()],
      nextControlId: 2,
    },
  ],
  activePlayerId: 1,
  isRolling: false,
}

const limitHistoryToMaxGroups = (history, maxGroups) => {
  if (history.length === 0) return history

  const groups = {}
  history.forEach(record => {
    const timestamp = record.groupTimestamp
    if (!groups[timestamp]) {
      groups[timestamp] = []
    }
    groups[timestamp].push(record)
  })

  const sortedTimestamps = Object.keys(groups)
    .map(ts => parseInt(ts))
    .sort((a, b) => b - a)
  const keepTimestamps = sortedTimestamps.slice(0, maxGroups)
  const keepTimestampSet = new Set(keepTimestamps)

  return history.filter(record => keepTimestampSet.has(record.groupTimestamp))
}

const addDiceRollRecord = (state, payload) => {
  const { playerId, rollData } = payload
  const updatedPlayers = state.players.map(player => {
    if (player.id === playerId) {
      const newHistory = [rollData, ...player.history]
      const limitedHistory = limitHistoryToMaxGroups(newHistory, MAX_GROUPS)
      return {
        ...player,
        history: limitedHistory,
      }
    }
    return player
  })

  const playerKey = `p${playerId}`
  const playerHistory = state[playerKey]?.history || []
  const newHistory = [rollData, ...playerHistory]
  const limitedHistory = limitHistoryToMaxGroups(newHistory, MAX_GROUPS)

  return {
    ...state,
    players: updatedPlayers,
    [playerKey]: {
      ...state[playerKey],
      history: limitedHistory,
    },
  }
}

const clearDiceRollHistory = (state, payload) => {
  const { playerId } = payload
  const updatedPlayers = state.players.map(player => {
    if (player.id === playerId) {
      return {
        ...player,
        history: [],
      }
    }
    return player
  })

  const playerKey = `p${playerId}`

  return {
    ...state,
    players: updatedPlayers,
    [playerKey]: {
      ...state[playerKey],
      history: [],
    },
  }
}

const addPlayer = (state, payload) => {
  return {
    ...state,
    players: [
      ...state.players,
      {
        ...payload,
        history: [],
        controls: [createDefaultControl()],
        dice: [],
        nextControlId: 2,
      },
    ],
  }
}

const updatePlayer = (state, payload) => {
  return {
    ...state,
    players: state.players.map(p =>
      p.id === payload.id ? { ...p, ...payload } : p,
    ),
  }
}

const deletePlayer = (state, payload) => {
  const playerId = payload

  if (state.players.length <= 1) {
    return state
  }

  return {
    ...state,
    players: state.players.filter(p => p.id !== playerId),
  }
}

const setActivePlayer = (state, payload) => {
  return {
    ...state,
    activePlayerId: payload,
  }
}

const updatePlayerControls = (state, payload) => {
  const { playerId, controls, nextControlId } = payload

  return {
    ...state,
    players: state.players.map(p =>
      p.id === playerId
        ? {
            ...p,
            controls: controls,
            nextControlId: nextControlId,
          }
        : p,
    ),
  }
}

const updateControlConfig = (state, payload) => {
  const { playerId, controlId, config } = payload

  return {
    ...state,
    players: state.players.map(p => {
      if (p.id !== playerId) return p

      const existingControl = p.controls.find(c => c.id === controlId)

      if (existingControl) {
        return {
          ...p,
          controls: p.controls.map(c =>
            c.id === controlId ? { ...c, ...config } : c,
          ),
        }
      } else {
        return {
          ...p,
          controls: [...p.controls, config],
        }
      }
    }),
  }
}

const updatePlayerDice = (state, payload) => {
  const { playerId, dice } = payload

  return {
    ...state,
    players: state.players.map(p =>
      p.id === playerId ? { ...p, dice: dice } : p,
    ),
  }
}

const setRollingState = (state, payload) => {
  return {
    ...state,
    isRolling: payload,
  }
}

export default createReducer(initialState, {
  [ADD_DICE_ROLL_RECORD]: addDiceRollRecord,
  [CLEAR_DICE_ROLL_HISTORY]: clearDiceRollHistory,
  [ADD_PLAYER]: addPlayer,
  [UPDATE_PLAYER]: updatePlayer,
  [DELETE_PLAYER]: deletePlayer,
  [SET_ACTIVE_PLAYER]: setActivePlayer,
  [UPDATE_PLAYER_CONTROLS]: updatePlayerControls,
  [UPDATE_CONTROL_CONFIG]: updateControlConfig,
  [UPDATE_PLAYER_DICE]: updatePlayerDice,
  [SET_ROLLING_STATE]: setRollingState,
})
